import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import DriveGrid from "./DriveGrid";
import GoogleDrivePicker from "./GoogleDrivePicker";
import GoogleDriveFolderPicker from "./GoogleDriveFolderPicker";
import GoogleDriveFolderBrowser from "./GoogleDriveFolderBrowser";
import { buildApiUrl, buildOAuthUrl } from "../utils/apiConfig";
import { isAthlete, isSignedInWithGoogle } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";
import "../styles/athlete-admin-layout.css";
import "../styles/drive.css";
import "../styles/pagination.css";
import { apiClient } from "../utils/apiClient";

type DriveFile = {
    id: string;
    name: string;
    mimeType: string;
    webViewLink?: string;
    thumbnailLink?: string | null;  // Backend provides thumbnailLink, not iconLink
    size?: number | null;
    modifiedTime?: string | null; // ISO string from backend
    isFolder?: boolean; // Backend also provides this field
};

type DriveListResponse = {
    files: DriveFile[];
    nextPageToken?: string | null;
    prevPageToken?: string | null;
    total?: number; // <-- Add this line
};

type ViewKind = "files" | "folders";

const FOLDER_MIME = "application/vnd.google-apps.folder";
const FILES_PER_PAGE = 5;

// Pagination state
type PaginationState = {
    currentPage: number;
    pageTokens: Map<number, string>; // Map of page number to pageToken
    totalPages?: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
};

function fmtDate(s?: string | null) {
    if (!s) return "—";
    try {
        const d = new Date(s);
        return d.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return s;
    }
}

function kindBadge(mime: string) {
    if (mime === FOLDER_MIME) return { label: "Folder", cls: "bdg-folder" };
    if (mime.includes("image/")) return { label: "Image", cls: "bdg-image" };
    if (mime.includes("video/")) return { label: "Video", cls: "bdg-video" };
    if (mime === "application/pdf") return { label: "Pdf", cls: "bdg-pdf" };
    if (mime === "application/vnd.google-apps.document")
        return { label: "Doc", cls: "bdg-doc" };
    if (mime === "application/vnd.google-apps.spreadsheet")
        return { label: "Sheet", cls: "bdg-sheet" };
    if (mime === "application/vnd.google-apps.presentation")
        return { label: "Slide", cls: "bdg-slide" };
    return { label: "File", cls: "bdg-generic" };
}

async function fetchDrive(
    view: ViewKind,
    pageToken?: string | null,
    folderId?: string | null,
    forceRefresh: boolean = false
): Promise<DriveListResponse> {
    const params = new URLSearchParams();
    if (pageToken) params.set("pageToken", pageToken);
    if (folderId) params.set("folderId", folderId);
    if (forceRefresh) params.set("t", Date.now().toString());

    // Add itemType parameter based on the view
    if (view === "files") {
        params.set("itemType", "files");
    } else if (view === "folders") {
        params.set("itemType", "folders");
    }

    // Use the backend endpoint: /api/user/drive/files (where saved files are stored)
    const url = buildApiUrl("api/user/drive/files");

    const res = await apiClient(`${url}?${params.toString()}`, {
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        credentials: "include", // Send HTTP-only cookies
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    // Handle both response formats: flat array or object with items property
    const rawItems = Array.isArray(data) ? data : (data.items || []);

    // Map backend fields to frontend expected fields
    const items = rawItems.map((item: any) => ({
        id: item.fileId || item.id,
        name: item.fileName || item.name,
        mimeType: item.mimeType,
        webViewLink: item.webViewLink,
        thumbnailLink: item.iconUrl || item.thumbnailLink,
        size: item.sizeBytes || item.size,
        modifiedTime: item.grantedAt || item.modifiedTime,
        isFolder: item.mimeType === FOLDER_MIME
    }));

    return {
        files: items,
        nextPageToken: data.nextPageToken,
        prevPageToken: data.prevPageToken,
    };
}

export default function DrivePage() {
    const { googleConnected } = useAuth();
    const { athleteId } = useParams<{ athleteId?: string }>();
    const location = useLocation();
    const [view, setView] = useState<ViewKind>("files");
    const [items, setItems] = useState<DriveFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string>("");
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationState>({
        currentPage: 1,
        pageTokens: new Map(),
        hasNextPage: false,
        hasPrevPage: false,
    });
    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;
    const [driveMenuOpenId, setDriveMenuOpenId] = useState<string | null>(null);
    const [removingId, setRemovingId] = useState<string | null>(null);
    const [totalFiles, setTotalFiles] = useState<number>(0);
    const [pickerSuccess, setPickerSuccess] = useState<string>("");
    const [folderBrowserData, setFolderBrowserData] = useState<any>(null);
    const [showFolderBrowser, setShowFolderBrowser] = useState(false);

    // Check if current user is admin/staff viewing an athlete drive
    const isAdminViewingAthlete = !isAthlete() && athleteId;

    // Get team member info from navigation state
    const navState = location.state as { teamMemberId?: string; teamMemberName?: string } | null;

    const signedInWithGoogle = googleConnected || isSignedInWithGoogle();

    // Handle picker file selection
    const handleFilesSelected = (files: any[]) => {
        setPickerSuccess(`Successfully added ${files.length} file(s) to your Drive`);
        // Refresh the file list after selection with forceRefresh to bypass cache
        setTimeout(() => {
            fetchPage(1, undefined, true);
        }, 500);
        // Clear success message after 5 seconds
        setTimeout(() => setPickerSuccess(""), 5000);
    };

    // Handle folder selection
    const handleFolderSelected = (response: any) => {
        setFolderBrowserData(response);
        setShowFolderBrowser(true);
    };

    const handleCloseFolderBrowser = () => {
        setShowFolderBrowser(false);
        setFolderBrowserData(null);
    };

    const handleImportFiles = async (fileIds: string[]) => {
        setPickerSuccess(`Successfully imported ${fileIds.length} file(s) from Google Drive`);
        // Refresh the file list after import
        setTimeout(() => {
            fetchPage(1, undefined, true);
        }, 500);
        // Clear success message after 5 seconds
        setTimeout(() => setPickerSuccess(""), 5000);
    };

    const handlePickerError = (error: string) => {
        setErr(error);
    };

    const handleFolderClick = async (folder: DriveFile) => {
        setLoading(true);
        try {
            const response = await apiClient(buildApiUrl("api/user/drive/folder/select"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    folderId: folder.id,
                    folderName: folder.name
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to load folder: ${response.statusText}`);
            }

            const rawData = await response.json();

            // Map backend field names to frontend expected format
            const mappedFiles = (rawData.files || []).map((file: any) => ({
                id: file.fileId || file.id,
                name: file.fileName || file.name,
                mimeType: file.mimeType,
                size: file.sizeBytes || file.size || 0,
                createdTime: file.createdTime || file.grantedAt || file.modifiedTime,
            }));

            const folderData = {
                folderId: rawData.folderId,
                folderName: rawData.folderName,
                files: mappedFiles,
                totalFiles: rawData.totalFiles || mappedFiles.length,
            };

            setFolderBrowserData(folderData);
            setShowFolderBrowser(true);
        } catch (error: any) {
            setErr(error.message || "Failed to load folder");
        } finally {
            setLoading(false);
        }
    };

    const fetchPage = async (page: number, pageToken?: string | null, forceRefresh: boolean = false) => {
        setLoading(true);
        setErr("");
        try {
            const data = await fetchDrive(view, pageToken, currentFolderId, forceRefresh);
            const files = data.files || [];
            setItems(files);
            // If backend provides total, use it; otherwise fallback to items.length
            setTotalFiles(typeof data.total === "number" ? data.total : files.length);
            setPagination((prev) => {
                const newPageTokens = new Map(prev.pageTokens);
                // Store the token for the next page if it exists
                if (data.nextPageToken) {
                    newPageTokens.set(page + 1, data.nextPageToken);
                }
                return {
                    ...prev,
                    currentPage: page,
                    hasNextPage: !!data.nextPageToken,
                    hasPrevPage: page > 1,
                    pageTokens: newPageTokens,
                };
            });
        } catch (e: any) {
            setErr(e?.message || "Failed to load Drive.");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveFromView = async (fileId: string) => {
        setRemovingId(fileId);
        setErr("");
        try {
            const res = await apiClient(buildApiUrl(`api/user/drive/items/${encodeURIComponent(fileId)}`), {
                method: "DELETE",
                credentials: "include",
            });
            if (!res.ok) {
                if (res.status === 404) {
                    throw new Error("Item not found");
                }
                throw new Error(`Failed to remove item (${res.status})`);
            }
            setDriveMenuOpenId(null);
            const p = paginationRef.current;
            const pageToken = p.currentPage === 1 ? undefined : p.pageTokens.get(p.currentPage);
            await fetchPage(p.currentPage, pageToken, true);
        } catch (e: any) {
            setErr(e?.message || "Failed to remove item");
        } finally {
            setRemovingId(null);
        }
    };

    useEffect(() => {
        if (driveMenuOpenId === null) return;
        const close = () => setDriveMenuOpenId(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [driveMenuOpenId]);

    useEffect(() => {
        // Don't make API calls if user hasn't signed in with Google
        if (!signedInWithGoogle) {
            setItems([]);
            setPagination({
                currentPage: 1,
                pageTokens: new Map(),
                hasNextPage: false,
                hasPrevPage: false,
            });
            setLoading(false);
            setErr("");
            return;
        }

        // Reset to first page when view changes
        setPagination({
            currentPage: 1,
            pageTokens: new Map(),
            hasNextPage: false,
            hasPrevPage: false,
        });
        fetchPage(1);
    }, [view, signedInWithGoogle, currentFolderId]);

    const goToPage = (page: number) => {
        if (page === pagination.currentPage) return;

        // For page 1, don't use a token. For other pages, use stored token
        const pageToken = page === 1 ? undefined : pagination.pageTokens.get(page);
        fetchPage(page, pageToken);
    };

    const goToNextPage = () => {
        if (pagination.hasNextPage) {
            goToPage(pagination.currentPage + 1);
        }
    };

    const goToPrevPage = () => {
        if (pagination.hasPrevPage) {
            goToPage(pagination.currentPage - 1);
        }
    };

    const emptyMsg = useMemo(() => {
        if (loading) return "";
        if (items.length > 0) return "";
        return view === "folders" ? "No folders found." : "No files found.";
    }, [items.length, loading, view]);

    // Pagination component
    const PaginationControls = () => {
        // Hide when there is nothing to show. During initial load, loading is true but items are
        // still [] — do not flash pagination (previously we only hid when !loading, which showed
        // controls for the whole loading phase). While paging, items stay populated until replaced.
        if (items.length === 0) return null;
        const { currentPage, hasNextPage, hasPrevPage } = pagination;

        // Generate page numbers based on what we know exists
        const getPageNumbers = () => {
            const pages = [];
            const maxVisiblePages = 5; // Show up to 5 page buttons

            // Always show pages from 1 to current page
            for (let i = 1; i <= currentPage; i++) {
                pages.push(i);
            }

            // If there's a next page, show it too
            if (hasNextPage && !pages.includes(currentPage + 1)) {
                pages.push(currentPage + 1);
            }

            // Optionally show a few more pages if we have tokens for them
            const maxPage = Math.max(currentPage + 1, ...Array.from(pagination.pageTokens.keys()));
            for (let i = currentPage + 2; i <= Math.min(maxPage, currentPage + maxVisiblePages - 1); i++) {
                if (pagination.pageTokens.has(i)) {
                    pages.push(i);
                }
            }

            return pages;
        };

        return (
            <div className="pagination">
                <div className="pagination-info">
                    <span>
                        Showing <strong>{(currentPage - 1) * FILES_PER_PAGE + 1}-{(currentPage - 1) * FILES_PER_PAGE + items.length}</strong> of <strong>{totalFiles}</strong> items
                    </span>
                </div>

                <div className="pagination-controls">
                    <div className="page-nav">
                        <button
                            className="page-btn"
                            onClick={goToPrevPage}
                            disabled={!hasPrevPage || loading}
                            aria-label="Previous page"
                        >
                            Previous
                        </button>
                        {getPageNumbers().map((page) => (
                            <button
                                key={page}
                                className={`page-number ${page === currentPage ? "page-number-active" : ""}`}
                                onClick={() => goToPage(page)}
                                disabled={loading}
                                aria-label={`Page ${page}`}
                                aria-current={page === currentPage ? 'page' : undefined}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className="page-btn"
                            onClick={goToNextPage}
                            disabled={!hasNextPage || loading}
                            aria-label="Next page"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Google Drive sync component
    const GoogleDriveSync = () => {
        const googleHref = buildOAuthUrl("google");

        return (
            <div className="google-drive-sync">
                <div className="sync-illustration">
                    <div className="empty-state-drive-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="7" y="4" width="10" height="16" rx="1.5" fill="#d8dce3" stroke="#c0c5ce" strokeWidth="1.5"/>
                            <rect x="8" y="1.5" width="8" height="3" rx="1" fill="#bcc1ca" stroke="#a8adb7" strokeWidth="1"/>
                            <circle cx="12" cy="3" r="0.75" fill="#9499a3"/>
                        </svg>
                    </div>
                </div>
                <h2 className="sync-title">Sync your Google Drive</h2>
                <p className="sync-description">
                    Start uploading or creating files to keep everything organized in one place from class notes and recruiting materials to videos and resumes.
                </p>
                <a href={googleHref} className="btn-sign-in-google">
                    Sign in with Google
                </a>
            </div>
        );
    };

    return (
        <>
            <DriveContent
                signedInWithGoogle={signedInWithGoogle}
                GoogleDriveSync={GoogleDriveSync}
                view={view}
                setView={setView}
                err={err}
                loading={loading}
                items={items}
                kindBadge={kindBadge}
                FOLDER_MIME={FOLDER_MIME}
                fmtDate={fmtDate}
                emptyMsg={emptyMsg}
                PaginationControls={PaginationControls}
                pickerSuccess={pickerSuccess}
                handleFilesSelected={handleFilesSelected}
                handleFolderSelected={handleFolderSelected}
                handleFolderClick={handleFolderClick}
                handlePickerError={handlePickerError}
                driveMenuOpenId={driveMenuOpenId}
                setDriveMenuOpenId={setDriveMenuOpenId}
                removingId={removingId}
                onRemoveFromView={handleRemoveFromView}
            />
            {showFolderBrowser && folderBrowserData && (
                <GoogleDriveFolderBrowser
                    initialFolder={folderBrowserData}
                    onClose={handleCloseFolderBrowser}
                    onImportFiles={handleImportFiles}
                    onError={handlePickerError}
                />
            )}
        </>
    );
}

// Separate component for the drive content to avoid duplication
function DriveContent({
    signedInWithGoogle,
    GoogleDriveSync,
    view,
    setView,
    err,
    loading,
    items,
    kindBadge,
    FOLDER_MIME,
    fmtDate,
    emptyMsg,
    PaginationControls,
    pickerSuccess,
    handleFilesSelected,
    handleFolderSelected,
    handleFolderClick,
    handlePickerError,
    driveMenuOpenId,
    setDriveMenuOpenId,
    removingId,
    onRemoveFromView,
}: any) {
    return (
        <section className="gd-wrap">
            {!signedInWithGoogle ? (
                // Show Google sign-in prompt if not authenticated
                <GoogleDriveSync />
            ) : (
                <>
                    <div className="gd-toolbar">
                        {view === "files" ? (
                            <a
                                href="https://drive.google.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="view-in-drive-link"
                                aria-label="View in Google Drive"
                            >
                                View in Google Drive ↗
                            </a>
                        ) : (
                            <div className="gd-toggle" role="tablist" aria-label="Drive view">
                                <button
                                    role="tab"
                                    aria-selected={view === "files"}
                                    className={`gd-toggle-btn ${view === "files" ? "is-active" : ""}`}
                                    onClick={() => setView("files")}
                                    title="Files"
                                >
                                    {/* File icon */}
                                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                        <path
                                            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.6"
                                        />
                                        <path
                                            d="M14 2v6h6"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.6"
                                        />
                                    </svg>
                                </button>
                                <button
                                    role="tab"
                                    aria-selected={view === "folders"}
                                    className={`gd-toggle-btn ${view === "folders" ? "is-active" : ""}`}
                                    onClick={() => setView("folders")}
                                    title="Folders"
                                >
                                    {/* Folder icon */}
                                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                        <path
                                            d="M3 6h6l2 2h10v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.6"
                                        />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* Add Google Drive Picker button - conditional based on view */}
                        <div className="gd-picker-container">
                            {view === "files" ? (
                                <GoogleDrivePicker
                                    onFilesSelected={handleFilesSelected}
                                    onError={handlePickerError}
                                />
                            ) : (
                                <GoogleDriveFolderPicker
                                    onFolderSelected={handleFolderSelected}
                                    onError={handlePickerError}
                                />
                            )}
                        </div>
                    </div>

                    {pickerSuccess && <div className="gd-success">{pickerSuccess}</div>}
                    {err && <div className="gd-alert">{err}</div>}

                    <div className="gd-grid" aria-busy={loading}>
                        {items.map((f: any) => {
                            const bdg = kindBadge(f.mimeType);
                            const isFolder = f.mimeType === FOLDER_MIME;

                            return (
                                <article className="gd-card" key={f.id}>
                                    <header className="gd-card-head">
                                        <span className={`gd-badge ${bdg.cls}`}>{bdg.label}</span>
                                        <div className="gd-card-menu-wrap">
                                            <button
                                                type="button"
                                                className="gd-dots"
                                                aria-label="More options"
                                                aria-expanded={driveMenuOpenId === f.id}
                                                aria-haspopup="menu"
                                                disabled={!!removingId}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDriveMenuOpenId((prev: string | null) =>
                                                        prev === f.id ? null : f.id
                                                    );
                                                }}
                                            >
                                                ⋮
                                            </button>
                                            {driveMenuOpenId === f.id && (
                                                <div
                                                    className="gd-card-menu"
                                                    role="menu"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="gd-card-menu-item"
                                                        disabled={removingId === f.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRemoveFromView(f.id);
                                                        }}
                                                    >
                                                        {removingId === f.id ? "Removing…" : "Remove from Polaris"}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </header>

                                    {isFolder ? (
                                        <div
                                            className="gd-preview gd-preview-clickable"
                                            onClick={() => handleFolderClick(f)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleFolderClick(f);
                                                }
                                            }}
                                            title={`Open ${f.name}`}
                                        >
                                            <div className="gd-preview-folder-icon">
                                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                                                </svg>
                                            </div>
                                        </div>
                                    ) : (
                                        <a
                                            className="gd-preview"
                                            href={f.webViewLink || `https://drive.google.com/file/d/${f.id}/view`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={f.name}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const url = f.webViewLink || `https://drive.google.com/file/d/${f.id}/view`;
                                                window.open(url, '_blank', 'noopener,noreferrer');
                                            }}
                                        >
                                            {f.thumbnailLink ? (
                                                <img src={f.thumbnailLink} alt="" />
                                            ) : (
                                                <div className="gd-preview-placeholder" />
                                            )}
                                        </a>
                                    )}

                                    <div className="gd-title" title={f.name}>
                                        {f.name || "Untitled"}
                                    </div>

                                    <footer className="gd-meta">
                                        <div className="gd-meta-row">
                                            <span>Last modified</span>
                                            <span>{fmtDate(f.modifiedTime)}</span>
                                        </div>
                                    </footer>
                                </article>
                            );
                        })}
                    </div>

                    {emptyMsg && <div className="gd-empty">{emptyMsg}</div>}

                    <PaginationControls />
                </>
            )}
        </section>
    );
}
